// src/pages/admin/CompanyManage.js
// 后台 · 管理中心 · 外部公司（doc01 3.4，缺陷 117/118）
// 结构：筛选（公司名称模糊）+ 查询·重置；表上方「关联外部公司」。
//  列：公司名称｜社会信用代码｜关联/创建时间｜联系人｜联系电话｜操作。
//  操作：详情｜权限分配｜解除关联（当前按钮名不符 → 已改为文档口径）。
//  关联：弹窗输入社会信用代码 → 已使用轻舟则显示公司名 → 确定 → 建立双向关联。
//  权限分配：选择站点（多选，"只可分配本公司创建的内部站点"）+ 电站权限（查看/控制，默认查看）。
//  解除关联：任一方解除则相互授权取消。
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Modal, Space, Select, Checkbox, Popconfirm, message } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { H_COMPANIES, OWN_STATIONS } from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const today = () => new Date().toISOString().slice(0, 10);
// 演示目录：已使用轻舟系统、可被关联的公司（输入其社会信用代码即可查询到公司名称）
const DIRECTORY = [
  { credit: '91330200MA2H8K21X', name: '宁波绿能科技' },
  { credit: '91330100MA2H76K08M', name: '温州智储电力' },
];

const CompanyManage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/admin/company');

  const [companies, setCompanies] = useState(() => H_COMPANIES.map((c) => ({ ...c })));
  const [fName, setFName] = useState('');
  const [applied, setApplied] = useState('');

  const [linkModal, setLinkModal] = useState({ open: false, credit: '', matched: null });
  const [detailModal, setDetailModal] = useState({ open: false, company: null });
  const [permModal, setPermModal] = useState({ open: false, company: null, stations: [], perms: ['查看'] });

  const rows = companies.filter((c) => !applied || c.name.includes(applied.trim()));
  const doQuery = () => { setApplied(fName); message.success('已按公司名称查询'); };
  const doReset = () => { setFName(''); setApplied(''); message.success('查询条件已重置'); };

  // ---------- 关联外部公司 ----------
  const openLink = () => setLinkModal({ open: true, credit: '', matched: null });
  const onCreditChange = (v) => {
    const credit = v.trim();
    const matched = DIRECTORY.find((d) => d.credit.toLowerCase() === credit.toLowerCase())
      || companies.find((c) => c.credit.toLowerCase() === credit.toLowerCase());
    setLinkModal((s) => ({ ...s, credit: v, matched: matched || null }));
  };
  const submitLink = () => {
    const credit = linkModal.credit.trim();
    if (!credit) { message.warning('请输入对方公司社会信用代码'); return; }
    if (!linkModal.matched) {
      message.warning('未查询到使用轻舟系统的公司；若对方未使用轻舟且本公司具备创建公司权限，可点击「下一步」创建合作公司。');
      return;
    }
    if (companies.some((c) => c.name === linkModal.matched.name)) { message.warning('该公司已关联'); return; }
    setCompanies((cs) => [...cs, {
      key: `c-${Date.now()}`, name: linkModal.matched.name, credit, linkedAt: today(),
      contact: '—', phone: '—', stations: 0, perms: '—', state: '合作中',
    }]);
    message.success(`已与「${linkModal.matched.name}」建立双向关联关系`);
    setLinkModal({ open: false, credit: '', matched: null });
  };
  const nextLink = () => {
    message.info('对方未使用轻舟系统：具备创建公司权限时可「下一步」创建合作公司，暂无该权限则无法关联。');
  };

  // ---------- 权限分配 ----------
  const openPerm = (c) => setPermModal({ open: true, company: c, stations: [], perms: ['查看'] });
  const togglePerm = (p, checked) => setPermModal((s) => ({
    ...s, perms: checked ? [...new Set([...s.perms, p])] : s.perms.filter((x) => x !== p),
  }));
  const savePerm = () => {
    if (!permModal.perms.length) { message.warning('请至少选择一项电站权限'); return; }
    setCompanies((cs) => cs.map((c) => (c.key === permModal.company.key
      ? { ...c, stations: permModal.stations.length, perms: permModal.perms.join(' / ') }
      : c)));
    message.success(`已为「${permModal.company.name}」分配 ${permModal.stations.length} 个站点，权限：${permModal.perms.join(' / ')}`);
    setPermModal((s) => ({ ...s, open: false }));
  };

  // ---------- 解除关联 ----------
  const unlink = (c) => {
    setCompanies((cs) => cs.filter((x) => x.key !== c.key));
    message.success(`已解除与「${c.name}」的关联，互相授权的电站权限均取消授权`);
  };

  const columns = [
    { title: '公司名称', dataIndex: 'name', render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '社会信用代码', dataIndex: 'credit' },
    { title: '关联/创建时间', dataIndex: 'linkedAt', width: 150 },
    { title: '联系人', dataIndex: 'contact' },
    { title: '联系电话', dataIndex: 'phone' },
    {
      title: '操作', key: 'op', fixed: 'right', width: 240,
      render: (_, r) => (
        <Space size={2} wrap>
          <Button type="link" size="small" onClick={() => setDetailModal({ open: true, company: r })}>详情</Button>
          <Button type="link" size="small" onClick={() => openPerm(r)}>权限分配</Button>
          <Popconfirm title={`确认解除与「${r.name}」的关联？`} onConfirm={() => unlink(r)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger>解除关联</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const d = detailModal.company || {};

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '外部公司'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel">
        <div className="panel-title">外部公司<small>共 {rows.length} 家公司</small></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>公司名称</span>
            <Input size="small" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="输入公司名称模糊查询" prefix={<SearchOutlined />} style={{ width: 220 }} allowClear />
          </span>
          <Space>
            <Button type="primary" size="small" onClick={doQuery}>查询</Button>
            <Button size="small" onClick={doReset}>重置</Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openLink}>关联外部公司</Button>
        </div>
        <Table rowKey="key" dataSource={rows} columns={columns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 1000 }} />
      </div>

      {/* 关联外部公司 */}
      <Modal title="关联外部公司" open={linkModal.open}
        onOk={submitLink} onCancel={() => setLinkModal({ open: false, credit: '', matched: null })} okText="确定" cancelText="取消" destroyOnHidden>
        <div style={{ marginBottom: 6, ...labelStyle }}>对方公司社会信用代码</div>
        <Input value={linkModal.credit} onChange={(e) => onCreditChange(e.target.value)} placeholder="请输入合作公司社会信用代码" />
        <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
          {linkModal.matched
            ? <span>已查询到公司：<b style={{ color: '#00d4ff' }}>{linkModal.matched.name}</b>，点击「确定」建立双向关联关系。</span>
            : (linkModal.credit
              ? <span>未查询到使用轻舟系统的公司。<Button type="link" size="small" style={{ padding: 0 }} onClick={nextLink}>下一步</Button>：若具备创建公司权限，可创建合作公司；无权限则无法关联。</span>
              : '输入对方社会信用代码，若已使用轻舟系统将展示对应公司名称。')}
        </div>
      </Modal>

      {/* 权限分配 */}
      <Modal title={`权限分配 · ${permModal.company ? permModal.company.name : ''}`} open={permModal.open}
        onOk={savePerm} onCancel={() => setPermModal((s) => ({ ...s, open: false }))} okText="确定" cancelText="取消" destroyOnHidden>
        <div style={{ marginBottom: 6, ...labelStyle }}>提示：只可分配本公司创建的内部站点</div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="选择要授权给该合作公司的站点"
          value={permModal.stations}
          onChange={(v) => setPermModal((s) => ({ ...s, stations: v }))}
          options={OWN_STATIONS.map((s) => ({ value: s.key, label: s.name }))}
        />
        <div style={{ margin: '14px 0 6px', ...labelStyle }}>电站权限（默认查看）</div>
        <Space size={16}>
          <Checkbox checked={permModal.perms.includes('查看')} disabled>查看</Checkbox>
          <Checkbox checked={permModal.perms.includes('控制')} onChange={(e) => togglePerm('控制', e.target.checked)}>控制</Checkbox>
        </Space>
      </Modal>

      {/* 详情 */}
      <Modal title={`外部公司详情 · ${d.name || ''}`} open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, company: null })}
        footer={<Button type="primary" onClick={() => setDetailModal({ open: false, company: null })}>关闭</Button>} destroyOnHidden>
        <div className="dev-kv">
          {[
            ['公司名称', d.name], ['社会信用代码', d.credit], ['关联/创建时间', d.linkedAt],
            ['联系人', d.contact], ['联系电话', d.phone], ['授权电站', d.stations != null ? `${d.stations} 个` : '-'],
            ['电站权限', d.perms], ['状态', d.state],
          ].map(([k, v]) => (
            <div className="dev-kv-row" key={k}>
              <span className="dev-kv-k">{k}</span>
              <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
            </div>
          ))}
        </div>
      </Modal>
    </PageSkeleton>
  );
};

export default CompanyManage;
